import { useState } from "react";
import { transparentize } from "polished";
import moment from "moment";
import { Box, Card, Chip, IconButton, Typography } from "@material-ui/core";
import { Color, EditFilledIcon, VerifiedIcon, WithPopperSimple } from "@bonliva-crm/ui";
import { professions, implicitToLocal } from "@bonliva-crm/common";
import { dateFormat } from "@bonliva-crm/locale";
import { ConsultantPipelineCardFragment } from "@graphql";
import { useLocale } from "@hooks";
import { QualityApprovalTooltip } from "@consultants/consultant-profile-new/personal-information/quality-approval-tooltip";
import { NationalQualityStatusIcon } from "@consultants/consultant-profile-new/personal-information/national-quality-status-icon";
import { CardDescription } from "../../kanban-board/kanban-types";
import { ConsultantPipelineCardMenu } from "./consultant-pipeline-card-menu";

type Props = {
  card: CardDescription<ConsultantPipelineCardFragment>;
  dragging: boolean;
  onCreateOpportunity: () => void;
  onOpenProfile: () => void;
  onUntagConsultant: () => void;
  onEditNextContactDate: () => void;
  onEditStatusNote: () => void;
  canUntagConsultant: boolean;
};

// NOTE: previously wrapped in `memo` with a comparator on `prev.card.issue
// === next.card.issue`. That comparator was permanently true once
// `applyCardsToBoard` / `upsertCardInBoard` started preserving wrapper
// identity (they mutate `existing.issue = card` on the SAME wrapper object,
// so prev.card and next.card are the same reference and `prev.card.issue`
// === `next.card.issue` is a tautology). The result was that after any
// consultant edit (e.g. changing "Nästa kontakt" via the card menu) the
// card's `bgColor` and the "Nästa kontakt" line stayed stale until the
// next hover/state change forced a re-render. Removing memo here keeps
// the card in sync with reducer updates; the parent column's draggable
// list already short-circuits when nothing changed.
export function ConsultantPipelineCard({ card, dragging, ...props }: Props) {
  const { locale } = useLocale();
  const [selected, setSelected] = useState<boolean>(false);

  const issue = card.issue;

  let weeks = issue.consultant?.Availability.availableWeeksString;
  weeks = weeks?.replaceAll("v. ", "v.\u00A0");

  const bgColor = getBgColor(issue.consultant?.nextContactDate?.date);

  return (
    <div
      style={{
        width: "285px",
        boxSizing: "border-box",
        margin: "4px 8px"
      }}
    >
      <Card
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          props.onOpenProfile();
        }}
        onMouseEnter={() => {
          setSelected(true);
        }}
        onMouseLeave={() => {
          setSelected(false);
        }}
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderRadius: "0.375rem",
          padding: "11px 17px",
          border: selected
            ? `1.5px solid ${Color.Information}`
            : `1px solid ${Color.LightGray}`,
          background: dragging ? transparentize(0.85, Color.PrimaryMain) : Color.OffWhite,
          boxShadow: `1px 6px 24px 0px ${Color.BoxShadow}`,
          backgroundColor: bgColor
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "0.5rem",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "32px"
          }}
        >
          <Typography
            variant="h6"
            style={{ fontWeight: 500 }}
            component="span"
            className="hl"
          >
            {card.title}
          </Typography>

          {selected && (
            <WithPopperSimple
              popperContent={({ close }) => (
                <ConsultantPipelineCardMenu
                  withNextContactDate={!!issue.consultant?.nextContactDate}
                  canUntag={issue.consultant && props.canUntagConsultant}
                  onOpenProfile={(e) => {
                    e.stopPropagation();
                    props.onOpenProfile();
                    setSelected(false);
                    close();
                  }}
                  onEditStatusNote={(e) => {
                    e.stopPropagation();
                    props.onEditStatusNote();
                    setSelected(false);
                    close();
                  }}
                  onCreateOpportunity={(e) => {
                    e.stopPropagation();
                    props.onCreateOpportunity();
                    setSelected(false);
                    close();
                  }}
                  onNextContactDate={(e) => {
                    e.stopPropagation();
                    props.onEditNextContactDate();
                    setSelected(false);
                    close();
                  }}
                  onUntag={(e) => {
                    e.stopPropagation();
                    props.onUntagConsultant();
                    setSelected(false);
                    close();
                  }}
                />
              )}
              placement="right-start"
            >
              {({ open }) => (
                <IconButton
                  style={{
                    backgroundColor: Color.PrimaryMain,
                    borderRadius: "0.25rem",
                    padding: "0.25rem",
                    minWidth: "2rem",
                    height: "2rem"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                >
                  <EditFilledIcon style={{ fill: Color.White }} />
                </IconButton>
              )}
            </WithPopperSimple>
          )}
        </Box>

        {issue.consultant && (
          <Box style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <Typography variant="body2" display="block">
              {issue.consultant.AreasOfExpertise.join(", ")}
            </Typography>

            <Typography variant="body2" display="block">
              {locale.Availability}:{" "}
              <span style={{ fontWeight: 500 }}>{weeks || "n/a"}</span>
            </Typography>
          </Box>
        )}

        {issue.consultant && (
          <Box style={{ display: "flex", alignItems: "center", height: "32px" }}>
            <QualityApprovalTooltip qualityApprovals={issue.consultant.qualityApprovals}>
              <Box style={{ display: "flex" }}>
                <VerifiedIcon
                  htmlColor={
                    issue.consultant.isQualityApproved &&
                    !issue.consultant.isQualityExpired
                      ? Color.CheckGreen
                      : Color.Error
                  }
                />
              </Box>
            </QualityApprovalTooltip>

            {professions.includesNationalProfession(issue.consultant.Professions) &&
            issue.consultant.nationalQualification ? (
              <QualityApprovalTooltip
                qualityApprovals={issue.consultant.nationalQualityApprovals}
              >
                <Box style={{ display: "flex" }}>
                  <NationalQualityStatusIcon
                    qualification={issue.consultant.nationalQualification}
                  />
                </Box>
              </QualityApprovalTooltip>
            ) : null}
          </Box>
        )}

        {issue.consultant?.nextContactDate && (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {locale.NextContact}:
            </Typography>

            {issue.consultant.nextContactDate.date && (
              <Typography variant="body2" component="span">
                {moment(implicitToLocal(issue.consultant.nextContactDate.date)).format(
                  dateFormat
                )}
              </Typography>
            )}

            {issue.consultant.nextContactDate.reason && (
              <Typography variant="body2" component="span">
                {" "}
                (
                {locale.nextContactDateReason[issue.consultant.nextContactDate.reason] ||
                  issue.consultant.nextContactDate.reason}
                )
              </Typography>
            )}
          </Box>
        )}

        {issue.description && (
          <Box>
            <Typography variant="body2">{issue.description}</Typography>
          </Box>
        )}

        {issue.consultant?.tags.length > 0 && (
          <Box
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.25rem",
              padding: "0",
              border: "1px solid transparent"
            }}
          >
            {issue.consultant.tags.map((tag) => (
              <Chip
                key={tag}
                size="small"
                variant="outlined"
                label={tag}
                style={{
                  padding: 0,
                  backgroundColor: transparentize(0.75, Color.DoveBlue)
                }}
              />
            ))}
          </Box>
        )}
      </Card>
    </div>
  );
}

function getBgColor(nextContactDate: Date | string | null) {
  let bgColor = "transparent";

  if (nextContactDate) {
    const d = moment(nextContactDate);
    const monthEnd = moment().endOf("month");

    if (d.isBefore(new Date(), "day")) {
      bgColor = transparentize(0.8, "red");
    } else if (d.isSame(new Date(), "day")) {
      bgColor = transparentize(0.8, "green");
    } else if (d.isBetween(new Date(), monthEnd)) {
      bgColor = transparentize(0.8, "yellow");
    }
  }

  return bgColor;
}
